// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Verificar autenticación al cargar o cambiar de ruta
  useEffect(() => {
    checkAuth();
  }, [location.pathname]);

  const checkAuth = () => {
    try {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      console.log('Verificando autenticación:', {
        hasToken: !!token,
        tokenLength: token?.length,
        hasUserData: !!storedUser,
        currentPath: location.pathname
      });

      // Borrar cualquier error previo
      setError(null);

      const path = (location.pathname || '').toLowerCase();
      const hostname = (window.location.hostname || '').toLowerCase();

      // Rutas públicas o portales independientes (Cliente, Técnico, Landing, Avisos) que NO deben redirigirse a /login
      const isPublicOrIndependentPath = 
        path === '/' || 
        path === '/signup' || 
        path.startsWith('/client') || 
        path.startsWith('/technician') || 
        path.startsWith('/aviso');

      // Detectar si estamos en el dominio principal de la Landing Page
      const isLandingDomain = 
        hostname === 'yinowisp.com' || 
        hostname === 'www.yinowisp.com' || 
        hostname === 'yinowisp.net' || 
        hostname === 'www.yinowisp.net';

      if (isPublicOrIndependentPath || (isLandingDomain && path === '/')) {
        setLoading(false);
        if (token && storedUser) {
          try {
            setUser(JSON.parse(storedUser));
          } catch (e) {}
        } else {
          setUser(null);
        }
        return;
      }

      if (!token || !storedUser) {
        // Solo redirigir al login si no estamos ya en /login
        if (!location.pathname.includes('/login')) {
          console.log('No hay token o datos de usuario, redirigiendo a login');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
          setLoading(false);
          navigate('/login', { replace: true });
        } else {
          setLoading(false);
        }
        return;
      }

      try {
        const userData = JSON.parse(storedUser);

        // Verificar estructura mínima de datos de usuario
        if (!userData || !userData.username || !userData.id) {
          throw new Error('Datos de usuario incompletos o inválidos');
        }

        // === VALIDACIÓN MULTI-TENANT POR SUBDOMINIO ===
        // Si estamos en un subdominio de inquilino (ej: alfa.yinowisp.com),
        // verificar que el token/usuario pertenece a ESE tenant
        const hostname = window.location.hostname.toLowerCase();
        const hostParts = hostname.split('.');
        const reservedSubdomains = ['www', 'admin', 'api', 'cliente', 'tecnico', 'aviso', 'mail', 'ftp', 'localhost'];

        if (hostParts.length >= 3 && !reservedSubdomains.includes(hostParts[0])) {
          const currentSubdomain = hostParts[0];
          
          // Admins y usuarios de sistema tienen acceso a cualquier subdominio
          const isGlobalUser = userData.role === 'admin' || userData.isSystemRole === true;
          
          if (!isGlobalUser && (!userData.tenant_slug || userData.tenant_slug !== currentSubdomain)) {
            console.warn(`[Multi-Tenant] Sesión no válida para subdominio "${currentSubdomain}". tenant_slug almacenado: "${userData.tenant_slug}". Forzando re-login.`);
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
            setLoading(false);
            if (!location.pathname.includes('/login')) {
              navigate('/login', { replace: true });
            }
            return;
          }

          console.log(`[Multi-Tenant] Sesión válida para subdominio "${currentSubdomain}"`);
        }
        // === FIN VALIDACIÓN MULTI-TENANT ===

        // Verificar que permissions sea un array
        if (userData.permissions && !Array.isArray(userData.permissions)) {
          console.warn('Permisos no es un array, corrigiendo formato');
          userData.permissions = [];
        }

        console.log('Usuario autenticado:', {
          username: userData.username,
          role: userData.role,
          isSystemRole: userData.isSystemRole,
          tenant_slug: userData.tenant_slug || 'N/A',
          permissionsCount: Array.isArray(userData.permissions) ? userData.permissions.length : 0
        });

        setUser(userData);
      } catch (error) {
        console.error('Error al procesar datos de usuario:', error);
        setError('Error al procesar datos de usuario: ' + error.message);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        if (!location.pathname.includes('/login')) {
          navigate('/login', { replace: true });
        }
      }
    } catch (error) {
      console.error('Error general en checkAuth:', error);
      setError('Error al verificar autenticación: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const login = (userData, token) => {
    console.log('Login exitoso, guardando datos:', {
      username: userData.username,
      tokenLength: token?.length,
      role: userData.role,
      permissionsCount: Array.isArray(userData.permissions) ? userData.permissions.length : 0
    });

    try {
      // Asegurarse de que permissions sea un array
      if (!Array.isArray(userData.permissions)) {
        userData.permissions = [];
      }

      // Guardar en localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));

      // Eliminar cualquier dato de cliente para evitar conflictos
      localStorage.removeItem('clientToken');
      localStorage.removeItem('clientData');

      setUser(userData);

      // Redireccionar al dashboard o a la ruta anterior (evitar la landing page)
      const from = location.state?.from?.pathname && location.state.from.pathname !== '/'
        ? location.state.from.pathname
        : '/admin';
      console.log('Redirigiendo a:', from);
      navigate(from, { replace: true });
    } catch (error) {
      console.error('Error guardando datos de autenticación:', error);
      setError('Error al guardar datos de sesión: ' + error.message);
    }
  };

  const logout = () => {
    console.log('Cerrando sesión');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/login', { replace: true });
  };

  // Verificar si el usuario tiene un permiso específico
  const hasPermission = (permission) => {
    // Si no hay usuario, no tiene permiso
    if (!user) return false;

    // Si es admin con rol de sistema, tiene todos los permisos
    if (user.role === 'admin' && user.isSystemRole === true) {
      return true;
    }

    // Verificar si el permiso está en el array de permisos
    return Array.isArray(user.permissions) && user.permissions.includes(permission);
  };

  const value = {
    user,
    login,
    logout,
    loading,
    error,
    refreshAuth: checkAuth,
    hasPermission
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};